import React from "react";
import { BatteryMedium, Thermometer, WifiHigh, WifiSlash, Gear } from "@phosphor-icons/react";
import { fmt, isSimulatorMode } from "../lib/api";
import { TargetChip } from "./TargetChip";

export const StatusBar = ({ state, onOpenSettings, onToggleConnect }) => {
  const connected = state?.connected;
  const battery = state?.battery ?? 0;
  const temp = state?.internal_temp ?? 0;
  const tiltMag =
    state ? Math.hypot(state.tilt_x || 0, state.tilt_y || 0) : 0;
  const sim = isSimulatorMode();

  return (
    <div
      data-testid="status-bar"
      className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--surface)] px-5 py-3"
    >
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          <img
            src="/fabaxis-mark-light.png"
            alt="FabAxis"
            data-testid="fabaxis-mark-statusbar"
            className="h-10 w-auto opacity-95"
          />
          <div className="hidden sm:block leading-tight">
            <div className="text-base font-bold uppercase tracking-widest" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              <span className="text-[#B0000A]">Fab</span>Axis
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[var(--text-dim)]">Field Controller</div>
          </div>
          <div
            data-testid="mode-badge"
            className={`ml-1 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
              sim
                ? "border-[var(--amber)] text-[var(--amber)]"
                : "border-[var(--neon)] text-[var(--neon)]"
            }`}
          >
            {sim ? "SIM" : "LIVE"}
          </div>
        </div>

        <div className="hidden items-center gap-2 sm:flex" data-testid="conn-indicator">
          <div className={`led ${connected ? "led-on" : "led-off"}`} />
          <div className="text-xs uppercase tracking-widest text-[var(--text-dim)]">
            {connected ? "LINK OK" : "OFFLINE"}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6 readout">
        <div className="flex items-center gap-2 text-sm" title="Battery">
          <BatteryMedium size={20} weight="bold" className="text-[var(--neon)]" />
          <span data-testid="battery-readout" className="tabular-nums">
            {fmt(battery, 0)}%
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm" title="Internal Temperature">
          <Thermometer size={20} weight="bold" className="text-[var(--amber)]" />
          <span data-testid="temp-readout" className="tabular-nums">
            {fmt(temp, 1)}°C
          </span>
        </div>
        <div className="hidden items-center gap-2 text-sm md:flex" title="Compensator tilt">
          <span className="text-[10px] tracking-widest text-[var(--text-dim)]">TILT</span>
          <span data-testid="tilt-readout" className="tabular-nums">
            {(tiltMag * 1000).toFixed(2)} mrad
          </span>
        </div>

        {/* Target / offset device chip — visible from every tab so the
            surveyor always knows what the app is correcting for. */}
        <TargetChip />

        <button
          data-testid="connect-btn"
          onClick={onToggleConnect}
          className="btn-tac !min-h-[44px] px-4 text-sm"
          data-variant={connected ? "danger" : "primary"}
        >
          {connected ? (
            <span className="flex items-center gap-2">
              <WifiSlash size={18} weight="bold" /> DISCONNECT
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <WifiHigh size={18} weight="bold" /> CONNECT
            </span>
          )}
        </button>

        <button
          data-testid="open-settings-btn"
          onClick={onOpenSettings}
          className="btn-tac !min-h-[44px] px-3 text-sm"
          aria-label="Settings"
        >
          <Gear size={20} weight="bold" />
        </button>
      </div>
    </div>
  );
};
